import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';

// =========================================================================
// 1. KHỞI TẠO CONTEXT
// =========================================================================
const ModalManagerContext = createContext();
export const useModalManager = () => useContext(ModalManagerContext);

// =========================================================================
// 2. COMPONENT BỌC LÓT (WRAPPER) - BẢO VỆ HIỆU NĂNG BẰNG REACT.MEMO
// =========================================================================
const ModalWrapper = React.memo(({ modal, index, closeModal }) => {
  const { Component, props, id } = modal;
  
  // Tính toán Z-Index (MUI Modal bắt đầu từ 1300, mỗi lớp cách nhau 10)
  const autoZIndex = 1300 + (index * 10);

  // Khóa chặt hàm đóng bằng useCallback để không thay đổi địa chỉ bộ nhớ
  const handleClose = useCallback(() => {
    closeModal(id);
  }, [closeModal, id]);

  return (
    <Component 
      key={id} 
      {...props} 
      
      open={true} // Ép thẻ MUI bật chế độ hiển thị
      onClose={handleClose} 
      
      // Bơm Z-Index đè lên các style cũ
      sx={{ ...props.sx, zIndex: autoZIndex }}
    />
  );
});

// =========================================================================
// 3. PROVIDER CHÍNH (NƠI ĐIỀU PHỐI MỌI HOẠT ĐỘNG)
// =========================================================================
export const ModalManagerProvider = ({ children }) => {
  // Mảng chứa các Component đang mở (Dành cho cách gọi openModal)
  const [modals, setModals] = useState([]);

  // =========================================================
  // HỆ THỐNG CẤP PHÁT Z-INDEX NGẦM (CHỐNG RE-RENDER)
  // =========================================================
  const zIndexStackRef = useRef([]); // Chứa danh sách ID của TẤT CẢ các form đang mở

  // Hàm xin cấp số (Dùng cho cả ManagedModal và form gọi qua Manager)
  const requestZIndex = useCallback((id) => {
    if (!zIndexStackRef.current.includes(id)) {
      zIndexStackRef.current.push(id);
    }
    // Trả về số thứ tự luôn (VD: 1300, 1310, 1320...)
    return 1300 + (zIndexStackRef.current.indexOf(id) * 10);
  }, []);

  // Hàm trả lại số khi form đóng
  const releaseZIndex = useCallback((id) => {
    zIndexStackRef.current = zIndexStackRef.current.filter(item => item !== id);
  }, []);

  // =========================================================
  // CÁC HÀM ĐIỀU KHIỂN CHÍNH (DÀNH CHO DEV GỌI)
  // =========================================================
  const openModal = useCallback((Component, props = {}) => {
    const id = `modal_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`; 
    
    setModals((prev) => [...prev, { id, Component, props }]);
    
    // Form sinh ra từ Manager cũng phải tuân thủ việc lấy số thứ tự
    requestZIndex(id);
    
    return id; 
  }, [requestZIndex]);

  const closeModal = useCallback((id = null) => {
    setModals((prev) => {
      if (prev.length === 0) return prev;
      
      // Nếu dev không truyền ID, tự động đóng cái trên cùng (cuối mảng)
      const targetId = id || prev[prev.length - 1].id;
      
      // Trả lại số thứ tự
      releaseZIndex(targetId);
      
      return prev.filter((modal) => modal.id !== targetId);
    });
  }, [releaseZIndex]);

  const closeAll = useCallback(() => {
    setModals([]);
    zIndexStackRef.current = []; // Reset sạch sẽ hệ thống
  }, []);

  // =========================================================
  // KHÓA CỨNG CONTEXT (BẢO VỆ CHỐNG INFINITE LOOP)
  // =========================================================
  const contextValue = useMemo(() => ({
    openModal, 
    closeModal, 
    closeAll, 
    requestZIndex, 
    releaseZIndex
  }), [openModal, closeModal, closeAll, requestZIndex, releaseZIndex]);

  // =========================================================
  // RENDER GIAO DIỆN
  // =========================================================
  return (
    <ModalManagerContext.Provider value={contextValue}>
      {children}

      {/* Render các form được gọi thông qua hàm openModal() */}
      {modals.map((modal) => {
        // Lấy vị trí thực tế trong mảng ngầm để cấp phát đúng Z-Index
        const actualIndex = zIndexStackRef.current.indexOf(modal.id);
        const safeIndex = actualIndex !== -1 ? actualIndex : zIndexStackRef.current.length;

        return (
          <ModalWrapper 
            key={modal.id} 
            modal={modal} 
            index={safeIndex} 
            closeModal={closeModal} 
          />
        );
      })}
    </ModalManagerContext.Provider>
  );
};